// src/services/errorTracking/ErrorTrackingService.js
import { logEvent } from 'firebase/analytics';
import { analytics } from '../config/firebase.js';

class ErrorTrackingService {
  constructor() {
    this.analytics = analytics;
    this.isEnabled = !!analytics && (
      import.meta.env.MODE === 'production' || 
      import.meta.env.VITE_ENABLE_CRASH_REPORTING === 'true'
    );
    this.errorQueue = [];
    this.maxQueueSize = 50;
    this.sessionId = this.generateSessionId();
    this.userId = null;
    this.userContext = {};
    
    // Set up global error handlers
    if (typeof window !== 'undefined') {
      this.setupGlobalErrorHandlers();
    }
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Set up global error handlers
   */
  setupGlobalErrorHandlers() {
    // Handle uncaught JavaScript errors
    window.addEventListener('error', (event) => {
      this.trackError({
        type: 'javascript_error',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        severity: 'error',
      });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError({
        type: 'unhandled_promise_rejection',
        message: event.reason?.message || 'Unhandled promise rejection',
        stack: event.reason?.stack,
        severity: 'error',
      });
    });

    // Handle React errors (if using React error boundaries)
    window.addEventListener('react-error', (event) => {
      this.trackError({
        type: 'react_error',
        message: event.detail?.message || 'React component error',
        stack: event.detail?.stack,
        componentStack: event.detail?.componentStack,
        severity: 'error',
      });
    });
  }

  /**
   * Set user context for error tracking
   */
  setUser(userId, userProperties = {}) {
    this.userId = userId;
    this.userContext = {
      userId,
      ...userProperties,
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Set additional context for error tracking
   */
  setContext(context) {
    this.userContext = {
      ...this.userContext,
      ...context,
    };
  }

  /**
   * Track error with comprehensive context
   */
  trackError(errorInfo) {
    if (!this.isEnabled) {
      console.warn('Error tracked (disabled):', errorInfo);
      return;
    }

    const enhancedError = {
      ...errorInfo,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.userId,
      userAgent: navigator.userAgent,
      url: window.location.href,
      referrer: document.referrer,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      context: this.userContext,
      errorId: this.generateErrorId(),
    };

    // Add to error queue
    this.errorQueue.push(enhancedError);
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift(); // Remove oldest error
    }

    // Send to Firebase Analytics
    this.sendErrorToAnalytics(enhancedError);

    // Log to console in development
    if (import.meta.env.MODE === 'development') {
      console.error('Error tracked:', enhancedError);
    }
  }

  /**
   * Generate unique error ID
   */
  generateErrorId() {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Send error to Firebase Analytics
   */
  sendErrorToAnalytics(errorInfo) {
    try {
      if (!this.analytics) return;

      // Clean data for Firebase Analytics
      const cleanError = {
        error_type: String(errorInfo.type || 'unknown'),
        error_message: String(errorInfo.message || 'No message').substring(0, 100),
        error_severity: String(errorInfo.severity || 'error'),
        error_filename: String(errorInfo.filename || 'unknown').substring(0, 100),
        error_line: Number(errorInfo.lineno || 0),
        error_session: String(this.sessionId),
        error_user: String(this.userId || 'anonymous'),
        error_url: String(window.location.pathname).substring(0, 100),
        error_timestamp: String(errorInfo.timestamp),
      };

      logEvent(this.analytics, 'app_error', cleanError);
    } catch (analyticsError) {
      console.warn('Failed to send error to analytics:', analyticsError);
    }
  }

  /**
   * Track performance issues
   */
  trackPerformanceIssue(performanceData) {
    this.trackError({
      type: 'performance_issue',
      message: `Performance issue: ${performanceData.metric}`,
      severity: 'warning',
      performanceData,
    });
  }

  /**
   * Track custom errors
   */
  trackCustomError(message, context = {}) {
    this.trackError({
      type: 'custom_error',
      message,
      severity: 'error',
      context,
    });
  }

  /**
   * Track warnings
   */
  trackWarning(message, context = {}) {
    this.trackError({
      type: 'warning',
      message,
      severity: 'warning',
      context,
    });
  }

  /**
   * Track API errors
   */
  trackAPIError(endpoint, statusCode, message, context = {}) {
    this.trackError({
      type: 'api_error',
      message: `API Error: ${endpoint} - ${message}`,
      severity: 'error',
      endpoint,
      statusCode,
      context,
    });
  }

  /**
   * Track Firebase errors
   */
  trackFirebaseError(operation, error, context = {}) {
    this.trackError({
      type: 'firebase_error',
      message: `Firebase ${operation}: ${error.message}`,
      severity: 'error',
      operation,
      firebaseCode: error.code,
      context,
    });
  }

  /**
   * Get error history for debugging
   */
  getErrorHistory() {
    return this.errorQueue.slice();
  }

  /**
   * Clear error history
   */
  clearErrorHistory() {
    this.errorQueue = [];
  }

  /**
   * Create crash report
   */
  createCrashReport() {
    const report = {
      sessionId: this.sessionId,
      userId: this.userId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      errors: this.errorQueue.slice(),
      context: this.userContext,
      performance: this.getPerformanceData(),
    };

    return report;
  }

  /**
   * Get basic performance data
   */
  getPerformanceData() {
    if (!window.performance) return null;

    try {
      const navigation = window.performance.getEntriesByType('navigation')[0];
      return {
        loadTime: navigation ? navigation.loadEventEnd - navigation.loadEventStart : 0,
        domContentLoaded: navigation ? navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart : 0,
        firstPaint: window.performance.getEntriesByName('first-paint')[0]?.startTime || 0,
        firstContentfulPaint: window.performance.getEntriesByName('first-contentful-paint')[0]?.startTime || 0,
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if error tracking is enabled
   */
  isErrorTrackingEnabled() {
    return this.isEnabled;
  }
}

// Create singleton instance
const errorTrackingService = new ErrorTrackingService();

export default errorTrackingService;